import { useEffect, useState, useMemo, useCallback } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Button,
  Input,
  Collapse,
} from 'reactstrap';

const PropertySelector = ({
  propertyList,
  selectedFilters,
  onPropertySelectionChange,
  onExportExcel,
  isLoading,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const currentProperty = useMemo(
    () => propertyList.find((prop) => prop._id === selectedFilters.property_id),
    [propertyList, selectedFilters.property_id]
  );

  const buildingList = useMemo(() => {
    if (!currentProperty?.building) return [];
    return currentProperty.building
      .filter((b) => b && b.buildingName)
      .map((b) => b.buildingName);
  }, [currentProperty]);

  const propertyOptions = useMemo(
    () =>
      propertyList.map((prop) => ({
        value: prop._id,
        label: `${prop.title}, ${prop.location || 'No location'}`,
      })),
    [propertyList]
  );

  const buildingOptions = useMemo(
    () =>
      buildingList.map((building) => ({
        value: building,
        label: building,
      })),
    [buildingList]
  );

  const selectedPropertyOption = useMemo(
    () =>
      propertyOptions.find((opt) => opt.value === selectedFilters.property_id) || null,
    [propertyOptions, selectedFilters.property_id]
  );

  const selectedBuildingOption = useMemo(
    () =>
      buildingOptions.find((opt) => opt.value === selectedFilters.building_name) || null,
    [buildingOptions, selectedFilters.building_name]
  );

  // Auto-select the only building when there's exactly one option
  useEffect(() => {
    if (
      buildingList.length === 1 &&
      !selectedFilters.building_name &&
      selectedFilters.property_id
    ) {
      onPropertySelectionChange({
        ...selectedFilters,
        building_name: buildingList[0],
      });
    }
  }, [buildingList, selectedFilters, onPropertySelectionChange]);

  const isExportDisabled = useMemo(() => {
    const hasProperty = Boolean(selectedFilters.property_id);
    const hasBuilding = Boolean(selectedFilters.building_name);

    // Disable if property selected but buildings exist and none chosen
    if (buildingList.length > 0 && !hasBuilding) {
      return true;
    }

    return !hasProperty;
  }, [selectedFilters.property_id, selectedFilters.building_name, buildingList.length]);

  const handlePropertyChange = useCallback(
    (selectedOption) => {
      const newPropertyId = selectedOption ? selectedOption.value : '';

      onPropertySelectionChange({
        ...selectedFilters,
        property_id: newPropertyId,
        building_name: '', // reset building when property changes
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const handleBuildingChange = useCallback(
    (selectedOption) => {
      const newBuildingName = selectedOption ? selectedOption.value : '';

      onPropertySelectionChange({
        ...selectedFilters,
        building_name: newBuildingName,
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const handleFilterChange = useCallback(
    (key) => (e) => {
      onPropertySelectionChange({
        ...selectedFilters,
        [key]: e.target.value || '',
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const toggleFilters = () => setIsFiltersOpen((prev) => !prev);

  const customSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, { isDisabled }) => ({
      ...base,
      zIndex: 2,
      backgroundColor: isDisabled ? '#f8f9fa' : base.backgroundColor,
      cursor: isDisabled ? 'not-allowed' : 'default',
    }),
  };

  return (
    <Form className="mb-4">
      <Row className="g-3 align-items-end">
        <Col md={5}>
          <FormGroup>
            <Label className="fw-semibold">Property</Label>
            <Select
              value={selectedPropertyOption}
              onChange={handlePropertyChange}
              options={propertyOptions}
              placeholder="Select Property"
              isClearable
              isDisabled={isLoading}
              styles={customSelectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </FormGroup>
        </Col>

        {buildingList.length > 0 && (
          <Col md={4}>
            <FormGroup>
              <Label className="fw-semibold">Building Name</Label>
              <Select
                value={selectedBuildingOption}
                onChange={handleBuildingChange}
                options={buildingOptions}
                placeholder="Select Building"
                isClearable={buildingList.length > 1}
                isDisabled={buildingList.length === 1 || isLoading}
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>
        )}

        <Col md={buildingList.length > 0 ? 3 : 7}>
          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <Button
              color="success"
              onClick={onExportExcel}
              disabled={isExportDisabled || isLoading}
              style={{ minWidth: '140px' }}
            >
              <FeatherIcon icon="download" size={18} className="me-2" />
              Export Excel
            </Button>

            <Button
              color="primary"
              outline
              onClick={toggleFilters}
              disabled={isLoading}
              style={{ minWidth: '140px' }}
            >
              <FeatherIcon icon="filter" size={18} className="me-2" />
              More Filters
            </Button>
          </div>
        </Col>
      </Row>

      <Collapse isOpen={isFiltersOpen}>
        <Row className="g-3 mt-3">
          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">From Date</Label>
              <Input
                type="date"
                value={selectedFilters.startDate || ''}
                onChange={handleFilterChange('startDate')}
                disabled={isLoading}
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">To Date</Label>
              <Input
                type="date"
                value={selectedFilters.endDate || ''}
                onChange={handleFilterChange('endDate')}
                disabled={isLoading}
                min={selectedFilters.startDate || undefined}
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">Overall Status</Label>
              <Select
                value={
                  selectedFilters.overall_status
                    ? {
                        value: selectedFilters.overall_status,
                        label:
                          selectedFilters.overall_status === 'passed'
                            ? 'Passed'
                            : 'Has Issues',
                      }
                    : null
                }
                onChange={(opt) =>
                  onPropertySelectionChange({
                    ...selectedFilters,
                    overall_status: opt?.value || '',
                  })
                }
                options={[
                  { value: 'passed', label: 'Passed' },
                  { value: 'has issues', label: 'Has Issues' },
                ]}
                placeholder="Filter by status..."
                isClearable
                isDisabled={isLoading}
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">Unit Number (partial)</Label>
              <Input
                type="text"
                value={selectedFilters.unit_number || ''}
                onChange={(e) =>
                  onPropertySelectionChange({
                    ...selectedFilters,
                    unit_number: e.target.value.trim(),
                  })
                }
                placeholder="e.g. 101, 12A"
                disabled={isLoading}
              />
            </FormGroup>
          </Col>
        </Row>
      </Collapse>
    </Form>
  );
};

export { PropertySelector };